import React, { PureComponent } from 'react'
import { Bar } from 'react-chartjs-2'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import styled from 'styled-components'
import max from 'lodash/max'

import { BlueBox, NoResults } from '../common'
import { Padding } from '../../../..'
import { colors } from '../../../../../constants'

const Header = styled.div`
    font-size: 15px;
    height: 50px;
`

const SubHeader = styled.span`
    color: ${colors.grey};
    margin-left: 10px;
`

const ChartContainer = styled.div`
    height: calc(100% - 60px);
    width: 100%;
`

const getDeltaSymbol = (delta) => {
    if (delta > 0) {
        return '^'
    }

    if (delta < 0) {
        return 'v'
    }

    return ''
}

class TopModels extends PureComponent {
    static propTypes = {
        isEmpty: PropTypes.bool,
        loading: PropTypes.number,
        models: PropTypes.arrayOf(
            PropTypes.shape({
                brand: PropTypes.string,
                delta: PropTypes.number,
                model: PropTypes.string,
                unique_visitors: PropTypes.number,
            })
        ),
        t: PropTypes.func.isRequired,
    }

    static defaultProps = {
        isEmpty: true,
        loading: null,
        models: [],
    }

    constructor(props) {
        super(props)

        this.primaryColor = colors.bluelight
        this.secondaryColor = colors.white

        this.deltaLabelFormatter = this.deltaLabelFormatter.bind(this)
    }

    /**
     * Gets colors array for every column in the chart
     */
    get backgroundColors() {
        const { models } = this.props
        const backgroundColors = models.map(({ delta }) => (!delta ? this.primaryColor : this.secondaryColor))

        return backgroundColors
    }

    /**
     * Gets values of the chart
     */
    get data() {
        const { models } = this.props
        return models.map(({ unique_visitors }) => unique_visitors)
    }

    /**
     * Gets lower model labels
     */
    get labels() {
        const { models } = this.props
        return models.map(({ model }) => model)
    }

    /**
     * Gets length of the data array
     */
    get length() {
        const { models } = this.props
        return models.length
    }

    /**
     * Gets the array with the upper labels color
     */
    get deltaLabelColors() {
        const { models } = this.props
        const colorsArray = models.map(({ delta }) => {
            if (delta > 0) {
                return colors.green
            }

            if (delta < 0) {
                return colors.red
            }

            return colors.white
        })

        return colorsArray
    }

    /**
     * Gets the formatted label for a particular value
     * @param {Number} value
     * @param {Object} context
     */
    deltaLabelFormatter(value, context) {
        const { models } = this.props
        const { dataIndex } = context
        const { brand, delta } = models[dataIndex]
        const deltaSymbol = getDeltaSymbol(delta)

        return `${deltaSymbol} ${brand}`
    }

    render() {
        const { isEmpty, loading, t } = this.props

        return (
            <BlueBox loading={loading} noResults={isEmpty}>
                <Header>
                    <Padding>
                        {t('Top Models')}
                        <SubHeader>(Interactions)</SubHeader>
                    </Padding>
                </Header>
                <ChartContainer>
                    <Padding bottom fullHeight>
                        {isEmpty && <NoResults />}
                        {!isEmpty && (
                            <Bar
                                data={{
                                    labels: this.labels,
                                    datasets: [
                                        {
                                            backgroundColor: this.backgroundColors,
                                            borderWidth: 0,
                                            hoverBackgroundColor: colors.white,
                                            data: this.data,
                                            pointRadius: 0,
                                            label: 'Interactions',
                                        },
                                    ],
                                }}
                                options={{
                                    bezierCurve: false,
                                    datasetFill: false,
                                    legend: {
                                        display: false,
                                    },
                                    maintainAspectRatio: false,
                                    plugins: {
                                        datalabels: {
                                            align: (context) => {
                                                // If value is above the top 90%, the label will be painted
                                                // inside the column
                                                const { dataIndex } = context
                                                const value = context.dataset.data[dataIndex]
                                                const maxValue = max(this.data)
                                                return value < maxValue * 0.9 ? 'end' : 'start'
                                            },
                                            anchor: 'end',
                                            color: this.deltaLabelColors,
                                            formatter: this.deltaLabelFormatter,
                                            offset: 5,
                                        },
                                    },
                                    scales: {
                                        xAxes: [
                                            {
                                                gridLines: {
                                                    color: 'transparent',
                                                },
                                                ticks: {
                                                    fontColor: colors.white,
                                                },
                                            },
                                        ],
                                        yAxes: [
                                            {
                                                gridLines: {
                                                    color: colors.bluedark,
                                                },
                                                ticks: {
                                                    fontColor: colors.white,
                                                },
                                            },
                                        ],
                                    },
                                }}
                            />
                        )}
                    </Padding>
                </ChartContainer>
            </BlueBox>
        )
    }
}

export default withTranslation()(TopModels)
